/**
 * Frontend Logger Utility
 * Provides consistent logging across the frontend with different log levels
 * Logs are stored in browser console and can be viewed in DevTools
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

class FrontendLogger {
  constructor() {
    // Set log level from environment or default to INFO in production, DEBUG in development
    this.logLevel = process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;
    this.maxStoredLogs = 100; // Keep last 100 logs in memory
    this.storedLogs = [];
  }

  /**
   * Format log message with timestamp and metadata
   */
  formatLog(level, message, metadata = {}) {
    const timestamp = new Date().toISOString();
    return {
      timestamp,
      level,
      message,
      ...metadata
    };
  }

  /**
   * Store log in memory (for debugging purposes)
   */
  storeLog(logEntry) {
    this.storedLogs.push(logEntry);
    // Keep only the most recent logs
    if (this.storedLogs.length > this.maxStoredLogs) {
      this.storedLogs.shift();
    }
  }

  /**
   * Debug level logging
   */
  debug(message, metadata = {}) {
    if (this.logLevel <= LOG_LEVELS.DEBUG) {
      const logEntry = this.formatLog('DEBUG', message, metadata);
      console.debug(`[${logEntry.timestamp}] [DEBUG]`, message, metadata);
      this.storeLog(logEntry);
    }
  }

  /**
   * Info level logging
   */
  info(message, metadata = {}) {
    if (this.logLevel <= LOG_LEVELS.INFO) {
      const logEntry = this.formatLog('INFO', message, metadata);
      console.info(`[${logEntry.timestamp}] [INFO]`, message, metadata);
      this.storeLog(logEntry);
    }
  }

  /**
   * Warning level logging
   */
  warn(message, metadata = {}) {
    if (this.logLevel <= LOG_LEVELS.WARN) {
      const logEntry = this.formatLog('WARN', message, metadata);
      console.warn(`[${logEntry.timestamp}] [WARN]`, message, metadata);
      this.storeLog(logEntry);
    }
  }

  /**
   * Error level logging
   */
  error(message, metadata = {}) {
    if (this.logLevel <= LOG_LEVELS.ERROR) {
      const logEntry = this.formatLog('ERROR', message, metadata);
      console.error(`[${logEntry.timestamp}] [ERROR]`, message, metadata);
      this.storeLog(logEntry);
    }
  }

  /**
   * Get all stored logs
   */
  getLogs() {
    return this.storedLogs;
  }

  /**
   * Clear stored logs
   */
  clearLogs() {
    this.storedLogs = [];
  }

  /**
   * Export logs as JSON string (useful for debugging)
   */
  exportLogs() {
    return JSON.stringify(this.storedLogs, null, 2);
  }
}

// Create singleton instance
const logger = new FrontendLogger();

// Make logger available globally in development for debugging
if (process.env.NODE_ENV === 'development') {
  window.logger = logger;
}

export default logger;
